import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI, tokenManager } from '../../services/api';
import Modal from '../../components/common/Modal';
import FireworksOverlay from '../../components/common/FireworksOverlay';
import ImageEditor from './ImageEditor';
import './AIImagePage.css';

// API 配置
const CREATE_API_URL = process.env.REACT_APP_CREATE_API_URL || 'http://172.16.16.16:3100/create';
const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || 'http://49.51.249.24:8000/upload';
const AUTH_API_KEY = process.env.REACT_APP_AUTH_API_KEY || 'AAAAC3NzaC1lZDI1NTE5AAAAIJA++TLRFQ2wBlxC+BIM1W1E8p4FnwvBMqnPKgiXp0XT';
const GET_IP_API_URL = process.env.REACT_APP_GET_IP || 'http://172.16.16.16:3100/getip';
const PROXY_DOWN_IMG_API_URL = process.env.REACT_APP_PROXY_DOWN_IMG_API_URL || 'http://172.16.16.16:3100/proxy_down_img';

const ASPECT_RATIOS = [
  { value: 'auto', label: '自动' },
  { value: '1:1', label: '1:1 正方形' },
  { value: '16:9', label: '16:9 横向' },
  { value: '9:16', label: '9:16 纵向' },
  { value: '4:3', label: '4:3 经典' },
  { value: '3:4', label: '3:4 纵向' },
  { value: '3:2', label: '3:2 照片' },
  { value: '2:3', label: '2:3 纵向' },
  { value: '5:4', label: '5:4 中等' },
  { value: '4:5', label: '4:5 Instagram' },
  { value: '21:9', label: '21:9 电影' },
];

const IMAGE_SIZES = [
  { value: '1K', label: '1K（标准）' },
  { value: '2K', label: '2K（高清）' },
  { value: '4K', label: '4K（超清）' },
];

function AIImagePage() {
  const { isAuthenticated, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('auto');
  const [imageSize, setImageSize] = useState('1K');
  const [imgUrls, setImgUrls] = useState([]);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadProgress, setImageLoadProgress] = useState(0);
  const [networkStatus, setNetworkStatus] = useState('loading');
  const [internalIp, setInternalIp] = useState('');
  const [externalIp, setExternalIp] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [isUploadingEdited, setIsUploadingEdited] = useState(false);
  const [showEditHintToast, setShowEditHintToast] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const imageRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasShownEditHintToastRef = useRef(false);

  const handleFuClick = useCallback(() => {
    setShowFireworks(false);
    window.setTimeout(() => setShowFireworks(true), 0);
  }, []);

  const isJwtExpired = (token) => {
    try {
      const parts = String(token).split('.');
      if (parts.length !== 3) return false;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
      const payload = JSON.parse(decodeURIComponent(escape(window.atob(padded))));
      if (!payload || typeof payload.exp !== 'number') return false;
      const nowSeconds = Math.floor(Date.now() / 1000);
      return payload.exp <= nowSeconds;
    } catch (e) {
      // 解析失败就不做“过期”判断，避免误伤
      return false;
    }
  };

  const promptLoginAndRedirect = (message) => {
    setError(message);
    // 清理本地 token，避免“看起来已登录但实际不可用”的状态
    tokenManager.clearTokens();
    // 带上 from，登录后可回到当前页面
    navigate('/login', { replace: true, state: { from: location } });
  };

  const ensureAuthenticated = async () => {
    if (!isAuthenticated) {
      setError('请先登录后再使用该功能');
      return false;
    }

    const accessToken = tokenManager.getAccessToken();
    if (!accessToken) {
      setError('请先登录后再使用该功能');
      return false;
    }

    // access token 过期时，尝试用 refresh 保活；若失败则提示重新登录
    if (isJwtExpired(accessToken)) {
      try {
        await authAPI.getProfile(); // 走 axios 拦截器，必要时会自动 refresh
        await refreshUser();
        return true;
      } catch (e) {
        promptLoginAndRedirect('登录已过期，请重新登录后使用');
        return false;
      }
    }

    return true;
  };

  // 网络状态检测
  useEffect(() => {
    const fetchIps = async () => {
      setNetworkStatus('loading');
      const [internalResult, externalResult] = await Promise.allSettled([
        fetch(GET_IP_API_URL).then(res => res.ok ? res.json() : Promise.reject('Internal IP fetch failed')),
        fetch('http://49.51.249.24:8000/ip').then(res => res.ok ? res.json() : Promise.reject('External IP fetch failed'))
      ]);
      
      const internalOk = internalResult.status === 'fulfilled';
      const externalOk = externalResult.status === 'fulfilled';
      
      if (internalOk) setInternalIp(internalResult.value.ip || '');
      if (externalOk) setExternalIp(externalResult.value.ip || '');
      
      if (internalOk && externalOk) setNetworkStatus('connected');
      else if (internalOk || externalOk) setNetworkStatus('partial');
      else setNetworkStatus('error');
    };
    fetchIps();
  }, []);

  // 首次成功上传参考图后，底部提示“点击图片可编辑”（仅显示一次）
  useEffect(() => {
    if (imgUrls.length > 0 && !hasShownEditHintToastRef.current) {
      hasShownEditHintToastRef.current = true;
      setShowEditHintToast(true);
    }
  }, [imgUrls.length]);

  // 自动隐藏提示
  useEffect(() => {
    if (!showEditHintToast) return;
    const t = setTimeout(() => setShowEditHintToast(false), 2800);
    return () => clearTimeout(t);
  }, [showEditHintToast]);

  const fetchImageWithProgress = async (url) => {
    setIsImageLoading(true);
    setImageLoadProgress(0);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`图片加载失败 (${response.status})`);

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      if (!response.body) {
        const blob = await response.blob();
        setImageLoadProgress(100);
        return URL.createObjectURL(blob);
      }

      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        if (total > 0) {
          setImageLoadProgress(Math.round((receivedLength / total) * 100));
        } else {
          setImageLoadProgress(prev => Math.min(prev + 5, 95));
        }
      }

      const blob = new Blob(chunks);
      setImageLoadProgress(100);
      return URL.createObjectURL(blob);
    } finally {
      setIsImageLoading(false);
    }
  };

  const fetchImageWithFallback = async (imageUrl, backupUrl, filename) => {
    try {
      return await fetchImageWithProgress(imageUrl);
    } catch (err) {
      const isCorsOrNetworkError = err instanceof TypeError || 
        err.message.includes('Failed to fetch') || 
        err.message.includes('NetworkError') ||
        err.message.includes('CORS');
      
      // 如果遇到跨域问题且有 filename，优先使用代理接口
      if (isCorsOrNetworkError && filename) {
        try {
          const proxyResponse = await fetch(PROXY_DOWN_IMG_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image_url: imageUrl,
              filename: filename
            }),
          });
          
          if (!proxyResponse.ok) {
            throw new Error(`代理接口请求失败 (${proxyResponse.status})`);
          }
          
          // 代理接口返回的是图片文件，需要转换为 blob URL
          const blob = await proxyResponse.blob();
          return URL.createObjectURL(blob);
        } catch (proxyErr) {
          // 代理接口失败，继续尝试备用链接
          console.warn('代理接口加载失败，尝试备用链接:', proxyErr);
        }
      }
      
      // 如果没有备用链接，提示用户稍后重试
      if (!backupUrl) {
        throw new Error('图片加载失败，请稍后重试');
      }
      
      // 尝试使用备用链接
      const maxRetries = 5;
      const retryDelay = 2000;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          return await fetchImageWithProgress(backupUrl);
        } catch (retryErr) {
          if (attempt === maxRetries) {
            throw new Error(`备用链接加载失败，已重试${maxRetries}次: ${retryErr.message}`);
          }
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
  };

  const handleGenerate = async () => {
    const ok = await ensureAuthenticated();
    if (!ok) return;

    if (!prompt.trim()) {
      setError('请输入提示内容');
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedImage(null);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => prev >= 90 ? prev : prev + Math.random() * 10);
    }, 500);

    try {
      const payload = { prompt: prompt.trim(), aspectRatio, imageSize };
      if (imgUrls.length > 0) payload.img_url = imgUrls;
      // 附带用户信息（用于后端日志记录；不影响生成接口本身）
      payload.user = {
        id: user?.id ?? user?.user_id ?? null,
        username: user?.username ?? null,
        email: user?.email ?? null,
        real_name: user?.real_name ?? null,
      };

      const response = await fetch(CREATE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMsg = `生成失败 (${response.status})`;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMsg = errorData.detail || errorData.error || errorMsg;
          }
        } catch (e) {}
        throw new Error(errorMsg);
      }

      const data = await response.json();
      let imageUrl = data.image_url || (data.result && data.result.image_url);
      let backupUrl = data.backup || (data.result && data.result.backup);
      let filename = data.filename || (data.result && data.result.filename);

      if (imageUrl) {
        setProgress(100);
        const localImageUrl = await fetchImageWithFallback(imageUrl, backupUrl, filename);
        setGeneratedImage(localImageUrl);
      } else {
        throw new Error('未知的响应格式，无法提取图片');
      }
    } catch (err) {
      setError(err.message || '生成图像失败');
    } finally {
      clearInterval(progressInterval);
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `ai-generated-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) handleGenerate();
  };

  const handleFiles = async (files) => {
    const ok = await ensureAuthenticated();
    if (!ok) return;

    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;

    setIsUploading(true);
    setError(null);

    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', AUTH_API_KEY);

        const uploadPromise = new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          // 如果后端需要 cookie 认证，启用 withCredentials
          xhr.withCredentials = true;
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              setUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
          });
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                if (data.success && data.url) resolve(data.url);
                else reject(new Error('上传失败：无效响应'));
              } catch (err) {
                reject(new Error('解析响应失败'));
              }
            } else {
              reject(new Error(`上传失败 (${xhr.status})`));
            }
          });
          xhr.addEventListener('error', () => reject(new Error('上传请求失败')));
          xhr.open('POST', UPLOAD_API_URL);
          xhr.send(formData);
        });

        const url = await uploadPromise;
        setImgUrls(prev => [...prev, url]);
        setUploadProgress(0);
      } catch (err) {
        setError(`上传 ${file.name} 失败：${err.message}`);
      }
    }

    setIsUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileUpload = (e) => handleFiles(e.target.files);
  const handleDragEnter = (e) => { if (!isAuthenticated) return; e.preventDefault(); e.stopPropagation(); setIsDragActive(true); };
  const handleDragOver = (e) => { if (!isAuthenticated) return; e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; setIsDragActive(true); };
  const handleDragLeave = (e) => { if (!isAuthenticated) return; e.preventDefault(); e.stopPropagation(); setIsDragActive(false); };
  const handleDrop = (e) => {
    if (!isAuthenticated) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer?.files?.length > 0) handleFiles(e.dataTransfer.files);
  };
  const handleRemoveImage = (index) => setImgUrls(prev => prev.filter((_, i) => i !== index));
  
  const extractImageFromElement = async (imgElement, url) => {
    try {
      // 检查图片是否同源，避免 CORS 问题
      const imgUrl = new URL(url, window.location.href);
      const isSameOrigin = imgUrl.origin === window.location.origin;
      
      // 如果跨域，直接返回原始 URL（浏览器缓存会避免重新下载）
      if (!isSameOrigin) {
        return null;
      }
      
      // 创建一个临时 canvas 来提取图片数据
      const canvas = document.createElement('canvas');
      canvas.width = imgElement.naturalWidth || imgElement.width;
      canvas.height = imgElement.naturalHeight || imgElement.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imgElement, 0, 0);
      
      // 转换为 blob URL，避免重新下载
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } else {
            // 如果转换失败，回退到原始 URL
            resolve(null);
          }
        }, 'image/png');
      });
    } catch (error) {
      // 如果出现 CORS 错误或其他问题，返回 null 使用原始 URL
      console.warn('Failed to extract image, using original URL:', error);
      return null;
    }
  };

  const handleImageClick = async (url, index, event) => {
    // 检查图片是否已加载完成
    const imgElement = event?.target;
    let imageDataUrl = url;
    
    if (imgElement && imgElement.tagName === 'IMG' && imgElement.complete) {
      // 图片已经加载完成，尝试从元素中提取数据（仅同源图片）
      // 如果是跨域图片，直接使用原始 URL（浏览器缓存会避免重新下载）
      try {
        const blobUrl = await extractImageFromElement(imgElement, url);
        if (blobUrl) {
          imageDataUrl = blobUrl;
        }
      } catch (error) {
        // 提取失败（可能是跨域），直接使用原始 URL
        // 由于图片已经在浏览器缓存中，不会重新下载
        imageDataUrl = url;
      }
    }
    // 如果图片未加载完成，也使用原始 URL
    // ImageEditor 会处理加载，但由于浏览器缓存，通常不会重新下载
    
    setSelectedImageUrl(imageDataUrl);
    setSelectedImageIndex(index);
    setIsEditingImage(true);
  };
  const handleCloseModal = () => {
    // 清理 blob URL（如果存在）
    if (selectedImageUrl && selectedImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(selectedImageUrl);
    }
    setSelectedImageUrl(null);
    setSelectedImageIndex(null);
    setIsEditingImage(false);
  };

  const uploadEditedImage = async (blob) => {
    const ok = await ensureAuthenticated();
    if (!ok) {
      setError('请先登录后再使用该功能');
      return;
    }

    setIsUploadingEdited(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', blob, 'edited-image.png');
      formData.append('api_key', AUTH_API_KEY);

      const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        // 如果后端需要 cookie 认证，启用 withCredentials
        xhr.withCredentials = true;
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.success && data.url) resolve(data.url);
              else reject(new Error('上传失败：无效响应'));
            } catch (err) {
              reject(new Error('解析响应失败'));
            }
          } else {
            reject(new Error(`上传失败 (${xhr.status})`));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('上传请求失败')));
        xhr.open('POST', UPLOAD_API_URL);
        xhr.send(formData);
      });

      const newUrl = await uploadPromise;
      
      // 清理旧的 blob URL（如果存在）
      if (selectedImageUrl && selectedImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(selectedImageUrl);
      }
      
      // 替换原图片 URL
      if (selectedImageIndex !== null) {
        setImgUrls(prev => {
          const newUrls = [...prev];
          newUrls[selectedImageIndex] = newUrl;
          return newUrls;
        });
      }

      // 关闭编辑器
      handleCloseModal();
      setUploadProgress(0);
    } catch (err) {
      setError(`上传编辑后的图片失败：${err.message}`);
    } finally {
      setIsUploadingEdited(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="ai-image-page">
      <FireworksOverlay active={showFireworks} durationMs={10000} onComplete={() => setShowFireworks(false)} />
      <div className="ai-bg">
        <div className="ai-bg-shape shape-1"></div>
        <div className="ai-bg-shape shape-2"></div>
        <div className="ai-bg-grid"></div>
      </div>

      {/* Navigation */}
      <nav className="ai-nav">
        <Link to="/" className="ai-nav-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          返回首页
        </Link>
        <div className="ai-nav-actions">
          {isAuthenticated ? (
            <Link to="/dashboard" className="ai-nav-user">
              {user?.real_name || user?.username}
            </Link>
          ) : (
            <Link to="/login" className="ai-nav-btn">登录</Link>
          )}
        </div>
      </nav>

      <div className="ai-container">
        {/* Header */}
        <header className="ai-header">
          
          <div className="ai-logo" onClick={handleFuClick}>
            <span className="ai-logo-icon">🖌️</span>
            <h1>AI 智能体改图</h1>
          </div>
          <p className="ai-tagline">将想象力转化为惊艳的视觉作品</p>
        </header>

        {/* Main Content */}
        <main className="ai-main">
          {/* Input Section */}
          <section className="ai-input-section">
            {!isAuthenticated && (
              <div className="ai-auth-notice">
                <div className="ai-auth-notice-text">
                  <strong>提示：</strong>登录后才可以上传参考图并生成图片。
                </div>
                <button
                  type="button"
                  className="ai-auth-login-btn"
                  onClick={() => navigate('/login', { state: { from: location } })}
                >
                  去登录
                </button>
              </div>
            )}
            <div className="ai-prompt-container">
              <label className="ai-label">
                <span className="ai-label-icon">💭</span>
                描述您的构想
              </label>
              <textarea
                className="ai-prompt-input"
                placeholder="薄暮中的神秘森林，荧光蘑菇点缀其间，萤火虫飞舞，古树上刻有发光的符文..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={4}
              />
              <span className="ai-hint">按 Ctrl + Enter 生成</span>
            </div>

            {/* Parameters */}
            <div className="ai-params-grid">
              <div className="ai-param-group">
                <label className="ai-label">
                  <span className="ai-label-icon">📐</span>
                  宽高比
                </label>
                <select className="ai-select" value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                  {ASPECT_RATIOS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              <div className="ai-param-group">
                <label className="ai-label">
                  <span className="ai-label-icon">🖼️</span>
                  分辨率
                </label>
                <select className="ai-select" value={imageSize} onChange={(e) => setImageSize(e.target.value)}>
                  {IMAGE_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div className="ai-param-group full-width">
                <label className="ai-label">
                  <span className="ai-label-icon">📎</span>
                  参考图片
                  <span className="ai-optional">（可选）</span>
                </label>
                <div className="ai-upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="aiFileUpload"
                    className="ai-file-input"
                    accept="image/png,image/jpeg,image/jpg"
                    multiple
                    onChange={handleFileUpload}
                    disabled={isUploading || !isAuthenticated}
                  />
                  <label
                    htmlFor="aiFileUpload"
                    className={`ai-upload-label ${isUploading ? 'uploading' : ''} ${!isAuthenticated ? 'disabled' : ''} ${isDragActive ? 'dragover' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {isUploading ? (
                      <>
                        <span className="ai-spinner small"></span>
                        <span>上传中... {uploadProgress}%</span>
                      </>
                    ) : (
                      <>
                        <span className="ai-upload-icon">📤</span>
                        <span>点击或拖拽上传图片</span>
                        <span className="ai-upload-hint">PNG、JPG，最大30MB</span>
                      </>
                    )}
                  </label>
                </div>
                {imgUrls.length > 0 && (
                  <div className="ai-uploaded-images">
                    {imgUrls.map((url, index) => (
                      <div key={index} className="ai-uploaded-item">
                        <img 
                          src={url} 
                          alt={`参考图 ${index + 1}`} 
                          onClick={(e) => handleImageClick(url, index, e)}
                          style={{ cursor: 'pointer' }}
                          crossOrigin={url.startsWith('blob:') ? undefined : 'anonymous'}
                        />
                        <button className="ai-remove-btn" onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <button
              className={`ai-generate-btn ${isLoading ? 'loading' : ''}`}
              onClick={handleGenerate}
              disabled={isLoading || !isAuthenticated}
              title={!isAuthenticated ? '请先登录后使用' : undefined}
            >
              {isLoading ? (
                <>
                  <span className="ai-spinner"></span>
                  <span>生成中...</span>
                </>
              ) : (
                <>
                  <span className="ai-btn-icon">✨</span>
                  <span>{isAuthenticated ? '生成图像' : '请先登录'}</span>
                </>
              )}
            </button>

            {/* Progress */}
            {isLoading && (
              <div className="ai-progress-container">
                <div className="ai-progress-bar">
                  <div className="ai-progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <span className="ai-progress-text">{Math.round(progress)}%</span>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="ai-error">
                <span>⚠️</span>
                {error}
              </div>
            )}
          </section>

          {/* Output Section */}
          <section className="ai-output-section">
            <div className={`ai-image-display ${generatedImage ? 'has-image' : ''} ${isImageLoading ? 'loading-image' : ''}`}>
              {isImageLoading ? (
                <div className="ai-image-loading">
                  <div className="ai-loading-spinner"></div>
                  <p>图片加载中...</p>
                  <div className="ai-loading-progress">
                    <div className="ai-loading-bar">
                      <div className="ai-loading-fill" style={{ width: `${imageLoadProgress}%` }}></div>
                    </div>
                    <span>{imageLoadProgress}%</span>
                  </div>
                </div>
              ) : generatedImage ? (
                <>
                  <img 
                    ref={imageRef} 
                    src={generatedImage} 
                    alt="生成的作品" 
                    className="ai-generated-image" 
                    crossOrigin={generatedImage.startsWith('blob:') ? undefined : 'anonymous'}
                  />
                  <div className="ai-image-overlay">
                    <button className="ai-download-btn" onClick={handleDownload}>
                      <span>⬇</span>
                      下载
                    </button>
                  </div>
                </>
              ) : (
                <div className="ai-placeholder">
                  <div className="ai-placeholder-icon">🎨</div>
                  <p>您的杰作将显示在此处</p>
                  <span>输入提示并点击生成</span>
                </div>
              )}
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="ai-footer">
          <p>由 Turen AI 提供支持 • 无限创作</p>
          <div className="ai-network-status" visibility='hidden'>
            <span className={`ai-status-light ${networkStatus}`}></span>
            <div className="ai-status-info">
              {networkStatus === 'loading' ? (
                <span>检测中...</span>
              ) : networkStatus === 'error' ? (
                <span className="error">网络不可用</span>
              ) : (
                <>
                  <span className={internalIp ? '' : 'error'}>内网: {internalIp || '不可用'}</span>
                  <span className={externalIp ? '' : 'error'}>外网: {externalIp || '不可用'}</span>
                </>
              )}
            </div>
          </div>
        </footer>
      </div>

      {/* Image Preview Modal with Editor */}
      <Modal
        isOpen={selectedImageUrl !== null}
        onClose={handleCloseModal}
        size="large"
        showCloseButton={!isEditingImage}
        closeOnEsc={!isEditingImage}
      >
        {isEditingImage && selectedImageUrl ? (
          <ImageEditor
            imageUrl={selectedImageUrl}
            onSave={uploadEditedImage}
            onCancel={handleCloseModal}
            isUploading={isUploadingEdited}
            uploadProgress={uploadProgress}
          />
        ) : (
          <div className="ai-image-preview-modal">
            {selectedImageUrl && (
              <img 
                src={selectedImageUrl} 
                alt="预览图片" 
                crossOrigin={selectedImageUrl.startsWith('blob:') ? undefined : 'anonymous'}
              />
            )}
          </div>
        )}
      </Modal>

      {showEditHintToast && (
        <div className="ai-upload-edit-hint-toast" role="status" aria-live="polite">
          <span className="ai-upload-edit-hint-icon" aria-hidden="true">✨</span>
          <div className="ai-upload-edit-hint-text">
            <div className="ai-upload-edit-hint-title">上传成功</div>
            <div className="ai-upload-edit-hint-subtitle">点击图片即可编辑</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIImagePage;

